import { getCurrentInstance, onBeforeUnmount, ref, type Ref } from 'vue';
import type { Router } from 'vue-router';

export interface InAppHistory {
  /** There is an in-app entry behind the current one. */
  canGoBack: Ref<boolean>;
  /** There is an in-app entry ahead (the user went back and has not navigated since). */
  canGoForward: Ref<boolean>;
  back: () => void;
  forward: () => void;
}

/**
 * The sidebar's two history buttons (spec §4): back and forward through THIS
 * app's own history, forward dimmed when there is nothing ahead.
 *
 * vue-router records the neighbouring in-app locations on every history entry it
 * writes (`history.state.back` / `.forward`, null at the edges), so the answer is
 * read off the current entry rather than counted: a cross-app arrival starts with
 * no back, a fresh push clears forward — both without this composable keeping a
 * stack of its own that could drift from the browser's. Re-read after every
 * resolved navigation and on popstate (the browser's own buttons).
 */
export function useInAppHistory(router: Router): InAppHistory {
  const canGoBack = ref(false);
  const canGoForward = ref(false);

  function read() {
    if (typeof window === 'undefined') return;
    const state = window.history.state as { back?: unknown; forward?: unknown } | null;
    canGoBack.value = !!state?.back;
    canGoForward.value = !!state?.forward;
  }

  read();
  const stopAfterEach = router.afterEach(() => read());
  if (typeof window !== 'undefined') window.addEventListener('popstate', read);
  const dispose = () => {
    stopAfterEach();
    if (typeof window !== 'undefined') window.removeEventListener('popstate', read);
  };
  if (getCurrentInstance()) onBeforeUnmount(dispose);

  return {
    canGoBack,
    canGoForward,
    back: () => {
      if (canGoBack.value) router.back();
    },
    forward: () => {
      if (canGoForward.value) router.forward();
    },
  };
}
